import {
  XStack,
  SizableText,
  YStack,
  Stack,
  styled,
  YStackProps,
  TextArea,
} from '@mythical/ui'
import { secondaryColors } from '@mythical/ui-config/colors'
import useMutation from 'app/hooks/use-mutation'
import {
  createCommentMutation,
  editCommentMutation,
  editCommentMutationResult,
  commentMutationResult,
} from 'app/mutations/comments/comment'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'

import { Avatar } from '../primitive/Avatar'
import { Button } from '../primitive/Button'
import Spinner from '../primitive/Spinner'

const createQuery = createCommentMutation
const editQuery = editCommentMutation

const CommentWrapper = styled(YStack, {
  space: '$2',
  bg: '$secondary10',
  p: '$3',
  $gtXs: {
    p: '$4',
  },
  borderRadius: '$3',
})
interface CommentFormProps {
  isReply?: boolean
  parent?: string
  id?: string
  isEditing?: true
  comment?: string
  onSubmitted?: () => void
  onCancel?: () => void
}

export const CommentForm = observer(
  ({
    parent,
    id,
    isEditing,
    isReply,
    comment,
    onSubmitted,
    onCancel,
    ...props
  }: CommentFormProps & YStackProps) => {
    const { userStore } = useStores()
    const query = isEditing ? editQuery : createQuery
    const format = isEditing ? editCommentMutationResult : commentMutationResult
    const [input, setInput] = useState(comment || '')

    const { loading, error, makeRequest } = useMutation(query, format)

    const handleCancel = () => {
      setInput('')
      onCancel?.()
    }

    if (!parent && !id) {
      return null
    }

    const handleSubmit = async () => {
      const variables = {
        comment: input,
        id: '',
        parent: '',
      }

      if (isEditing) {
        variables.id = id || ''
      } else {
        variables.parent = parent || ''
      }

      const result = await makeRequest(variables)

      if (result && result.id) {
        if (!isEditing) {
          setInput('')
        }
        userStore?.refreshAccessToken()
        onSubmitted?.()
      }
    }

    return (
      <CommentWrapper
        space="$2"
        bg="$secondary10"
        p="$3"
        $gtXs={{
          p: '$4',
        }}
        borderRadius="$3"
        {...props}
      >
        <XStack space="$3" alignItems="flex-start">
          <Avatar
            src={userStore?.user?.profileImage}
            tier={userStore?.user?.tier}
            simple
            width={40}
            height={40}
          />
          <YStack flexGrow={1} flexShrink={1} space="$2">
            <TextArea
              placeholderTextColor={secondaryColors.secondary6}
              placeholder={isReply ? 'Reply...' : 'Add a comment...'}
              flex={1}
              size="$3"
              px="$2.5"
              py="$2"
              theme="outline"
              value={input}
              onChangeText={setInput}
              onSubmitEditing={handleSubmit}
            />

            <YStack jc="flex-start" space="$2" flexWrap="wrap">
              {input !== '' && (
                <>
                  <Stack flex={1} jc="center">
                    {error && (
                      <SizableText my="$1.5" color="$red6">
                        {error.message}
                      </SizableText>
                    )}
                  </Stack>
                  <XStack jc="flex-end" space="$2">
                    <Button theme="tertiary" onPress={handleCancel}>
                      Cancel
                    </Button>
                    <Button onPress={handleSubmit}>
                      {loading ? <Spinner secondary /> : 'Submit'}
                    </Button>
                  </XStack>
                </>
              )}
            </YStack>
          </YStack>
        </XStack>
      </CommentWrapper>
    )
  }
)
